import {
  createStyles,
  StylesClasses,
  STYLE_SIZE_CONSTANTS,
} from '@utils/styles'

const fullBleed = {
  width: '100vw',
  maxWidth: '100vw',
  marginLeft: 'calc(50% - 50vw)',
  marginRight: 'calc(50% - 50vw)',
  boxSizing: 'border-box',
} as const

const styles = createStyles({
  tabsSection: {
    ...fullBleed,
    position: { xs: 'relative', md: 'sticky' },
    top: { md: `${STYLE_SIZE_CONSTANTS.headerHeight}px` },
    zIndex: 200,
    backgroundColor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'border.light',
  },
  tabsInner: {
    width: '100%',
    maxWidth: '1248px',
    margin: '0 auto',
    padding: {
      xs: '0 24px',
      md: 0,
    },
    boxSizing: 'border-box',
  },
  wrapper: {
    width: '100%',
    maxWidth: '1248px',
    margin: '0 auto',
    padding: {
      xs: '16px 24px 64px',
      md: '20px 0 80px',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: '24px', md: '32px' },
    boxSizing: 'border-box',
  },
  searchPage: {
    width: '100%',
    backgroundColor: 'neutral.25',
  },
  resultsTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: '24px', md: '32px' },
    scrollMarginTop: {
      xs: `${STYLE_SIZE_CONSTANTS.mobileHeaderHeight + 16}px`,
      md: `${STYLE_SIZE_CONSTANTS.headerHeight + 64}px`,
    },
  },
  categoryTabs: {
    width: '100%',
    maxWidth: '100%',
    minHeight: 'unset',
    '& .MuiTabs-scroller': {
      overflowX: 'auto !important',
    },
    '& .MuiTabs-flexContainer': {
      width: {
        xs: '100%',
        md: 'auto',
      },
    },
    '& .MuiTab-root': {
      flex: {
        xs: '1 0 auto',
        md: '0 0 auto',
      },
      minWidth: {
        xs: 0,
        md: 'auto',
      },
      maxWidth: 'none',
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '14px',
      minHeight: 48,
    },
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  resultsList: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      lg: 'repeat(3, 1fr)',
    },
    gap: { xs: '24px', md: '32px' },
    width: '100%',
  },
  serviceResultsList: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'repeat(2, 1fr)',
    },
    gap: { xs: '16px', md: '24px' },
    width: '100%',
  },
  resourceResultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: '12px', md: '16px' },
    width: '100%',
  },
  resourceColumns: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'minmax(0, 1fr) minmax(0, 1fr)',
    },
    gap: { xs: '24px', md: '32px' },
    width: '100%',
    alignItems: 'start',
  },
  resourceColumn: {
    minWidth: 0,
    width: '100%',
  },
  resourceColumnDivided: {
    borderColor: 'border.light',
    borderStyle: 'solid',
    borderWidth: { xs: '1px 0 0 0', md: '0 0 0 1px' },
    paddingTop: { xs: '24px', md: 0 },
    paddingLeft: { xs: 0, md: '32px' },
  },
  resultItem: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    width: '100%',
    minWidth: 'unset',
    maxWidth: 'unset',
    // Override StandardCard `flex: 1 1 0` + `height: 100%` which collapse
    // when the parent has no explicit height.
    flex: 'none',
    height: 'auto',
    minHeight: '436px',
  },
  image: {
    height: '196px',
  },
  skeletonCard: {
    width: '100%',
    minHeight: '436px',
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: 'background.paper',
  },
  emptyAlert: {
    width: '100%',
    alignItems: 'center',
    padding: { xs: '12px 16px', md: '14px 20px' },
    borderRadius: '8px',
    boxShadow: 'none',
    '&.MuiAlert-standardError': {
      backgroundColor: 'custom.main',
      color: 'text.primary',
    },
    '& .MuiAlert-icon': {
      color: 'text.secondary',
      padding: 0,
      marginRight: '12px',
      opacity: 1,
    },
    '& .MuiAlert-message': {
      padding: 0,
      color: 'text.primary',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.5,
    },
  },
  defaultCardsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: '20px', md: '24px' },
    width: '100%',
  },
  resultSections: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  resultSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: '20px', md: '24px' },
    width: '100%',
    scrollMarginTop: {
      xs: `${STYLE_SIZE_CONSTANTS.mobileHeaderHeight + 16}px`,
      md: `${STYLE_SIZE_CONSTANTS.headerHeight + 64}px`,
    },
  },
  resultSectionBlock: {
    width: '100%',
    scrollMarginTop: {
      xs: `${STYLE_SIZE_CONSTANTS.mobileHeaderHeight + 16}px`,
      md: `${STYLE_SIZE_CONSTANTS.headerHeight + 64}px`,
    },
  },
  resultSectionBlockDivided: {
    borderTop: '1px solid',
    borderColor: 'border.light',
    paddingTop: { xs: '24px', md: '32px' },
    marginTop: { xs: '24px', md: '32px' },
  },
  defaultCardsAfterResults: {
    paddingTop: { xs: '16px', md: '28px' },
    marginTop: { xs: '8px', md: '12px' },
    borderTop: '1px solid',
    borderColor: 'border.light',
  },
  defaultCardsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    width: '100%',
  },
  defaultCardsHeading: {
    textTransform: 'uppercase',
    color: 'text.primary',
    letterSpacing: '0.04em',
  },
  viewAllButton: {
    minWidth: 'unset',
    height: 'auto',
    padding: '0 !important',
    color: 'primary.main',
    '&:hover': {
      backgroundColor: 'transparent !important',
      textDecoration: 'underline',
    },
  },
})

export type StylesClassNames = StylesClasses<typeof styles>

export default styles
